import { useState } from 'react';
import { useWhatsappChats } from '../whatsappWebApi';
import { Search, MoreVertical, MessageSquare, Users, CircleDashed, MessageSquarePlus, ListFilter, ArrowLeft } from 'lucide-react';
import { Spinner } from '@/shared/components/ui/Spinner';
import { cn } from '@/shared/utils/utils';

interface ChatSidebarProps {
  selectedChat: string | null;
  onSelectChat: (id: string) => void;
  onDisconnect: () => void;
  me: { number: string; pushname: string; picUrl?: string } | null;
}

export function ChatSidebar({ selectedChat, onSelectChat, onDisconnect, me }: ChatSidebarProps) {
  const [search, setSearch] = useState('');
  const { data: chats = [], isLoading } = useWhatsappChats(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isNewChatOpen, setIsNewChatOpen] = useState(false);
  const [newChatNumber, setNewChatNumber] = useState('');

  const filteredChats = chats.filter(c => 
    (c.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (c.id || '').toLowerCase().includes(search.toLowerCase()) ||
    (c.lastMessage || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="flex h-full flex-col relative overflow-hidden">
      {/* Header */}
      <div className="flex h-[59px] shrink-0 items-center justify-between bg-[#f0f2f5] px-4 py-2.5 box-border">
        <div className="flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-full bg-[#dfe5e7] text-[#54656f]">
          {me?.picUrl ? (
            <img src={me.picUrl} alt="Avatar" className="h-full w-full object-cover" />
          ) : (
            <span className="text-body-lg font-medium">{me?.pushname?.charAt(0)?.toUpperCase() || 'U'}</span>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          <button className="flex h-10 w-10 items-center justify-center rounded-full text-[#54656f] transition-colors hover:bg-black/5" title="Community">
            <Users className="h-[22px] w-[22px]" />
          </button>
          <button className="flex h-10 w-10 items-center justify-center rounded-full text-[#54656f] transition-colors hover:bg-black/5" title="Status">
            <CircleDashed className="h-[22px] w-[22px]" />
          </button>
          <button 
            className="flex h-10 w-10 items-center justify-center rounded-full text-[#54656f] transition-colors hover:bg-black/5"
            onClick={() => setIsNewChatOpen(true)}
            title="New Chat"
          >
            <MessageSquarePlus className="h-[22px] w-[22px]" />
          </button>
          
          <div className="relative">
            <button 
              className={cn(
                "flex h-10 w-10 items-center justify-center rounded-full text-[#54656f] transition-colors hover:bg-black/5",
                menuOpen && "bg-black/10"
              )}
              onClick={() => setMenuOpen(!menuOpen)}
            >
              <MoreVertical className="h-[22px] w-[22px]" />
            </button>
            
            {menuOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
                <div className="absolute right-0 top-12 z-20 w-48 rounded-md bg-white py-2 shadow-[0_2px_5px_0_rgba(11,20,26,0.26),0_2px_10px_0_rgba(11,20,26,0.16)] text-[#3b4a54]">
                  <button 
                    className="flex w-full items-center px-6 py-3 text-left text-[14.5px] hover:bg-[#f5f6f6] transition-colors"
                    onClick={() => { setMenuOpen(false); onDisconnect(); }}
                  >
                    Log out
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="flex items-center gap-2 border-b border-[#e9edef] bg-white p-2 h-[49px] box-border">
        <div className="flex flex-1 items-center rounded-lg bg-[#f0f2f5] px-3 py-1.5 transition-all">
          <Search className="h-[18px] w-[18px] text-[#54656f]" />
          <input 
            type="text" 
            placeholder="Search or start new chat" 
            className="ml-4 w-full bg-transparent text-[15px] text-[#111b21] outline-none placeholder:text-[#54656f]"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <button className="flex h-8 w-8 items-center justify-center rounded-full text-[#54656f] hover:bg-black/5">
          <ListFilter className="h-5 w-5" />
        </button>
      </div>

      {/* Chat List */}
      <div className="flex-1 overflow-y-auto bg-white custom-scrollbar">
        {isLoading ? (
          <div className="flex h-32 items-center justify-center">
            <Spinner className="text-[#00a884]" />
          </div>
        ) : filteredChats.length === 0 ? (
          <div className="flex h-48 flex-col items-center justify-center text-[#54656f] p-4 text-center">
            <MessageSquare className="mb-2 h-8 w-8 opacity-40 text-[#00a884]" />
            <p className="text-[14px] font-medium text-[#111b21]">No chats found</p>
            <p className="text-[13px] text-[#667781] mt-1 max-w-[220px]">
              Start a new chat to send a WhatsApp message to any phone number.
            </p>
            <button 
              onClick={() => setIsNewChatOpen(true)}
              className="mt-4 rounded-full bg-[#00a884] px-4 py-2 text-[13px] font-medium text-white shadow-sm hover:bg-[#017561] transition-colors"
            >
              + Start New Chat
            </button>
          </div>
        ) : (
          filteredChats.map(chat => {
            const initial = (chat.name || '').replace(/^\+/, '').charAt(0).toUpperCase() || 'C';
            return (
              <button
                key={chat.id}
                onClick={() => onSelectChat(chat.id)}
                className={cn(
                  "flex w-full items-center gap-3 pr-3 pl-3 py-0 hover:bg-[#f5f6f6] transition-colors text-left group",
                  selectedChat === chat.id && "bg-[#f0f2f5] hover:bg-[#f0f2f5]"
                )}
              >
                <div className="flex h-[49px] w-[49px] shrink-0 items-center justify-center rounded-full bg-[#dfe5e7] text-[#54656f] overflow-hidden">
                  <span className="text-body-lg font-medium">{initial}</span>
                </div>
                
                <div className="flex flex-1 flex-col justify-center border-b border-[#f2f2f2] py-3 h-[72px] overflow-hidden">
                  <div className="flex items-center justify-between">
                    <span className="truncate text-[17px] font-normal text-[#111b21] leading-tight font-sans">
                      {chat.name || chat.id}
                    </span>
                    {chat.timestamp && (
                      <span className={cn(
                        "shrink-0 text-[12px]",
                        chat.unreadCount > 0 ? "text-[#00a884] font-medium" : "text-[#667781]"
                      )}>
                        {new Date(chat.timestamp * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <span className="truncate text-[14px] text-[#667781] leading-snug">
                      {chat.lastMessage || 'No recent messages'}
                    </span>
                    {chat.unreadCount > 0 && (
                      <span className="ml-2 flex h-[20px] min-w-[20px] items-center justify-center rounded-full bg-[#00a884] px-[0.4rem] text-[11px] font-bold text-white leading-none pb-[1px]">
                        {chat.unreadCount}
                      </span>
                    )}
                  </div>
                </div>
              </button>
            );
          })
        )}
      </div>

      {/* New Chat Panel */}
      {isNewChatOpen && (
        <div className="absolute inset-0 z-30 flex flex-col bg-white overflow-hidden animate-in slide-in-from-left-full duration-300">
          <div className="flex h-[108px] shrink-0 items-end bg-[#008069] px-[20px] pb-[16px] text-white gap-6">
            <button 
              onClick={() => setIsNewChatOpen(false)} 
              className="mb-1 text-white hover:text-white/80 transition-colors"
            >
              <ArrowLeft className="h-[24px] w-[24px]" />
            </button>
            <h2 className="text-[19px] font-medium leading-none mb-1">New chat</h2>
          </div>
          <div className="p-4 border-b border-[#e9edef] bg-white">
            <input 
              placeholder="Enter phone number (e.g. 91987654321)" 
              value={newChatNumber} 
              onChange={e => setNewChatNumber(e.target.value.replace(/[^0-9]/g, ''))} 
              className="w-full bg-[#f0f2f5] px-4 py-3 rounded-lg outline-none text-[#111b21] text-[15px]" 
              autoFocus
            />
            <p className="text-[13px] text-[#667781] mt-2 px-1">
              Include country code without '+' or '00'
            </p>
          </div>
          <div className="p-4">
            <button 
              disabled={!newChatNumber}
              onClick={() => { 
                onSelectChat(`${newChatNumber}@c.us`); 
                setIsNewChatOpen(false); 
                setNewChatNumber(''); 
                setSearch('');
              }}
              className="w-full bg-[#00a884] hover:bg-[#017561] text-white px-4 py-3 rounded-lg font-medium disabled:opacity-50 transition-colors shadow-sm"
            >
              Start Chat
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
